// src/pages/PositionnementDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { positionnementAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Save, X, MapPin, Building2, User,
  Calendar, Briefcase, RefreshCw, DollarSign,
} from 'lucide-react';
import TracabilityInfo from '../components/common/TracabilityInfo';

const ETATS = [
  'Positionné', 'Qualification en cours', 'Intercontrat',
  'Futur arrivé', 'Refusé', 'Abandonné',
];

const ETAT_COLORS = {
  'Positionné':             '#3B82F6',
  'Qualification en cours': '#F59E0B',
  'Intercontrat':           '#8B5CF6',
  'Futur arrivé':           '#10B981',
  'Refusé':                 '#EF4444',
  'Abandonné':              '#6B7280',
};

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:12 }}>
      <span style={{ color:'var(--text-muted)', flexShrink:0, marginTop:2 }}>{icon}</span>
      <div>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:1, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
        <div style={{ fontSize:'0.875rem', color:'var(--text-primary)' }}>{value}</div>
      </div>
    </div>
  );
}

export default function PositionnementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pos,     setPos]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await positionnementAPI.get(id);
        setPos(res.data);
      } catch {
        toast.error('Positionnement introuvable');
        navigate('/positionnements');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  const startEdit = () => {
    setForm({
      etat:                         pos.etat || '',
      etat_motif:                   pos.etat_motif || '',
      motif_commentaire:            pos.motif_commentaire || '',
      commentaires:                 pos.commentaires || '',
      cand_etape:                   pos.cand_etape || '',
      tarif_journalier_ht:          pos.tarif_journalier_ht || '',
      nb_jours_vendus:              pos.nb_jours_vendus || '',
      nb_jours_gratuits:            pos.nb_jours_gratuits || '',
      taux_occupation:              pos.taux_occupation || '',
      cjm:                          pos.cjm || '',
      chiffre_affaires_ht:          pos.chiffre_affaires_ht || '',
      marge_ht:                     pos.marge_ht || '',
      rentabilite:                  pos.rentabilite || '',
      responsable_manager_besoin:   pos.responsable_manager_besoin || '',
      responsable_rh_candidat:      pos.responsable_rh_candidat || '',
      responsable_manager_candidat: pos.responsable_manager_candidat || '',
      besoin_titre:                 pos.besoin_titre || '',
      besoin_type:                  pos.besoin_type || '',
      besoin_lieu:                  pos.besoin_lieu || '',
      societe_nom:                  pos.societe_nom || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await positionnementAPI.update(id, form);
      setPos(res.data);
      setEditing(false);
      toast.success('Positionnement mis à jour');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );
  if (!pos) return null;

  const etatColor = ETAT_COLORS[pos.etat] || '#6B7280';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}><ArrowLeft size={18}/></button>
          <div>
            <h1 style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              {pos.reference}
              <span style={{ whiteSpace:'nowrap', padding:'3px 12px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:etatColor+'22', color:etatColor }}>
                {pos.etat || '—'}
              </span>
            </h1>
            <p style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>
              {[pos.cand_prenom, pos.cand_nom].filter(Boolean).join(' ')}
              {pos.societe_nom && ` · ${pos.societe_nom}`}
            </p>
          </div>
        </div>
        <div className="page-header-actions">
          {!editing ? (
            <button className="btn btn-secondary" onClick={startEdit}><Edit2 size={15}/> Modifier</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}><X size={15}/> Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <div className="spinner" style={{ width:13, height:13 }}/> : <Save size={15}/>} Enregistrer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={pos.created_at || pos.date_creation}
          createdById={pos.created_by_user_id}
          updatedAt={pos.updated_at}
          updatedById={pos.updated_by_user_id}
          showWarningIfNoData
        />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20 }}>

        {/* Colonne gauche */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Candidat */}
          <div className="card">
            <div className="card-header"><span className="card-title">Candidat / Ressource</span></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px' }}>
              <InfoRow icon={<User size={14}/>}      label="Nom complet"    value={[pos.cand_prenom, pos.cand_nom].filter(Boolean).join(' ')}/>
              <InfoRow icon={<Briefcase size={14}/>} label="Référence"      value={pos.cand_ref}/>
              <InfoRow icon={<User size={14}/>}      label="Type"           value={pos.cand_type}/>
              <InfoRow icon={<User size={14}/>}      label="Profil type"    value={pos.profil_type}/>
              <InfoRow icon={<User size={14}/>}      label="Contact"        value={[pos.contact_prenom, pos.contact_nom].filter(Boolean).join(' ')}/>
              {editing ? (
                <div>
                  <label style={lbl}>Étape candidat</label>
                  <input style={inp} value={form.cand_etape} onChange={e => setForm(f => ({...f, cand_etape:e.target.value}))}/>
                </div>
              ) : (
                <InfoRow icon={<User size={14}/>} label="Étape candidat" value={pos.cand_etape}/>
              )}
            </div>
          </div>

          {/* Besoin */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Besoin associé</span>
              {pos.besoin_id && (
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/besoins/${pos.besoin_id}`)}>
                  Voir le besoin →
                </button>
              )}
            </div>
            {editing ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={lbl}>Titre besoin</label>
                  <input style={inp} value={form.besoin_titre} onChange={e => setForm(f => ({...f, besoin_titre:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Type besoin</label>
                  <input style={inp} value={form.besoin_type} onChange={e => setForm(f => ({...f, besoin_type:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Lieu</label>
                  <input style={inp} value={form.besoin_lieu} onChange={e => setForm(f => ({...f, besoin_lieu:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Société</label>
                  <input style={inp} value={form.societe_nom} onChange={e => setForm(f => ({...f, societe_nom:e.target.value}))}/>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px' }}>
                <InfoRow icon={<Briefcase size={14}/>} label="Titre"      value={pos.besoin_titre}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Référence"  value={pos.besoin_reference}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Type"       value={pos.besoin_type}/>
                <InfoRow icon={<MapPin size={14}/>}    label="Lieu"       value={pos.besoin_lieu}/>
                <InfoRow icon={<Building2 size={14}/>} label="Société"    value={pos.societe_nom}/>
                {pos.besoin_domaine_intervention && (
                  <div style={{ gridColumn:'1/-1' }}>
                    <InfoRow icon={<Briefcase size={14}/>} label="Domaine d'intervention" value={pos.besoin_domaine_intervention}/>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* État */}
          <div className="card">
            <div className="card-header"><span className="card-title">État du positionnement</span></div>
            {editing ? (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div>
                  <label style={lbl}>État</label>
                  <select style={inp} value={form.etat} onChange={e => setForm(f => ({...f, etat:e.target.value}))}>
                    <option value="">— Sélectionner —</option>
                    {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Motif état</label>
                  <input style={inp} value={form.etat_motif} onChange={e => setForm(f => ({...f, etat_motif:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Motif commentaire</label>
                  <textarea rows={3} style={{ ...inp, resize:'vertical' }} value={form.motif_commentaire} onChange={e => setForm(f => ({...f, motif_commentaire:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Commentaires</label>
                  <textarea rows={3} style={{ ...inp, resize:'vertical' }} value={form.commentaires} onChange={e => setForm(f => ({...f, commentaires:e.target.value}))}/>
                </div>
              </div>
            ) : (
              <>
                <InfoRow icon={<Briefcase size={14}/>} label="Motif état" value={pos.etat_motif}/>
                {pos.motif_commentaire && (
                  <div style={{ padding:'10px 14px', background:'rgba(245,158,11,0.06)', borderRadius:8, border:'1px solid rgba(245,158,11,0.15)', fontSize:'0.875rem', lineHeight:1.7, marginBottom:10 }}>
                    {pos.motif_commentaire}
                  </div>
                )}
                {pos.commentaires && (
                  <div style={{ padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)', fontSize:'0.875rem', lineHeight:1.7 }}>
                    {pos.commentaires}
                  </div>
                )}
                {!pos.etat_motif && !pos.motif_commentaire && !pos.commentaires && (
                  <div style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucun commentaire</div>
                )}
              </>
            )}
          </div>

          {/* Financier */}
          <div className="card">
            <div className="card-header"><span className="card-title">Données financières</span></div>
            {editing ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                {[
                  ['TJM HT',         'tarif_journalier_ht'],
                  ['Jours vendus',   'nb_jours_vendus'],
                  ['Jours gratuits', 'nb_jours_gratuits'],
                  ['CA HT',          'chiffre_affaires_ht'],
                  ['Marge HT',       'marge_ht'],
                  ['Rentabilité',    'rentabilite'],
                  ['Taux occupation','taux_occupation'],
                  ['CJM',            'cjm'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label style={lbl}>{label}</label>
                    <input style={inp} value={form[key]} onChange={e => setForm(f => ({...f, [key]:e.target.value}))}/>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0 24px' }}>
                <InfoRow icon={<DollarSign size={14}/>} label="TJM HT"         value={pos.tarif_journalier_ht ? `${pos.tarif_journalier_ht} ${pos.devise || ''}`.trim() : null}/>
                <InfoRow icon={<DollarSign size={14}/>} label="Jours vendus"   value={pos.nb_jours_vendus}/>
                <InfoRow icon={<DollarSign size={14}/>} label="Jours gratuits" value={pos.nb_jours_gratuits}/>
                <InfoRow icon={<DollarSign size={14}/>} label="CA HT"          value={pos.chiffre_affaires_ht}/>
                <InfoRow icon={<DollarSign size={14}/>} label="Marge HT"       value={pos.marge_ht}/>
                <InfoRow icon={<DollarSign size={14}/>} label="Rentabilité"    value={pos.rentabilite}/>
                <InfoRow icon={<DollarSign size={14}/>} label="Taux occupation" value={pos.taux_occupation}/>
                <InfoRow icon={<DollarSign size={14}/>} label="CJM"            value={pos.cjm}/>
              </div>
            )}
          </div>
        </div>

        {/* Colonne droite */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Responsables */}
          <div className="card">
            <div className="card-header"><span className="card-title">Responsables</span></div>
            {editing ? (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div>
                  <label style={lbl}>Resp. manager besoin</label>
                  <input style={inp} value={form.responsable_manager_besoin} onChange={e => setForm(f => ({...f, responsable_manager_besoin:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Resp. manager candidat</label>
                  <input style={inp} value={form.responsable_manager_candidat} onChange={e => setForm(f => ({...f, responsable_manager_candidat:e.target.value}))}/>
                </div>
                <div>
                  <label style={lbl}>Resp. RH candidat</label>
                  <input style={inp} value={form.responsable_rh_candidat} onChange={e => setForm(f => ({...f, responsable_rh_candidat:e.target.value}))}/>
                </div>
              </div>
            ) : (
              <>
                <InfoRow icon={<User size={14}/>} label="Resp. manager besoin"    value={pos.responsable_manager_besoin}/>
                <InfoRow icon={<User size={14}/>} label="Resp. manager candidat"  value={pos.responsable_manager_candidat}/>
                <InfoRow icon={<User size={14}/>} label="Resp. RH candidat"       value={pos.responsable_rh_candidat}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Agence"             value={pos.agence}/>
              </>
            )}
          </div>

          {/* Traçabilité */}
          <div className="card">
            <div className="card-header"><span className="card-title">Traçabilité</span></div>
            <InfoRow icon={<Calendar size={14}/>}  label="Date création (Boond)" value={pos.date_creation ? new Date(pos.date_creation).toLocaleDateString('fr-FR') : null}/>
            <InfoRow icon={<Calendar size={14}/>}  label="Date MAJ (Boond)"      value={pos.date_maj ? new Date(pos.date_maj).toLocaleDateString('fr-FR') : null}/>
            <InfoRow icon={<Calendar size={14}/>}  label="Créé dans l'app le"    value={fmtDate(pos.created_at)}/>
            <InfoRow icon={<RefreshCw size={14}/>} label="Dernière mise à jour"  value={fmtDate(pos.updated_at)}/>
            {pos.updated_by_name && (
              <InfoRow icon={<User size={14}/>} label="Modifié par" value={pos.updated_by_name}/>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
